import { ConfigProvider, Pagination, Select, Space } from "antd";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button as BsButton, Col, Container, Modal, Row } from "react-bootstrap";
import AppSearch from "../../common/AppSearch";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import itIT from "antd/lib/locale/it_IT";
import { Programmazione } from "../../types/Programmazione";
import { useNavigate } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { TailSpin } from "react-loader-spinner";
import { Programmazioni } from "../../api/Programmazioni";
import { Option, PagedResults } from "../../types/generics";
import { Users } from "../../api/Users";
import ProgrammazioneCard from "../../Components/ProgrammazioneCard";
import { useAuth } from "../../contexts/auth";
import { Roles } from "../../enums/Roles";

function ProgrammazioniList() {
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const { loggedUser } = useAuth();
  const canCreate = loggedUser?.role === Roles.Superadmin || loggedUser?.role === Roles.Trainer;
  const [error, setError] = useState(false);
  const [total, setTotal] = useState<number>();
  const [programmazioni, setProgrammazioni] = useState<Programmazione[]>();
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    freeText: string;
    userId?: number;
    parentId?: number;
  }>({
    pageSize: 20,
    currentPage: 1,
    freeText: "",
  });
  const [usersOptions, setUsersOptions] = useState<Option[]>();
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedProgrammazione, setSelectedProgrammazione] = useState<Programmazione | null>(null);
  const [loading, setLoading] = useState(false);

  const initClients = useCallback(() => {
    Users.getUsersForProgrammazioni().then(
      ({ data }) => {
        const options = data.list.map((user) => ({
          label: user.nome + " " + user.cognome,
          value: user.id!,
        }));
        setUsersOptions(options);
      },
      (err) => {
        showToast(
          "users-error",
          "Impossibile ottenere la lista degli Utenti",
          "",
          "danger"
        );
        setUsersOptions([]);
      }
    );
  }, [showToast]);

  const initProgrammazioni = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);
    if (filters.freeText !== "") urlParams.append("freeText", filters.freeText);
    if (!!filters.userId) urlParams.append("userId", `${filters.userId}`);
    if (!!filters.parentId) urlParams.append("parentId", `${filters.parentId}`);
    Programmazioni.getProgrammazioni(urlParams).then(
      ({ data }) => {
        setProgrammazioni((data as PagedResults<Programmazione>).items);
        setTotal((data as PagedResults<Programmazione>).totalItems);
      },
      (err) => {
        showToast(
          "programmazioni-error",
          "Impossibile ottenere la lista delle Programmazioni",
          "",
          "danger"
        );
        setProgrammazioni([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setProgrammazioni(undefined);
    setTotal(0);
    initProgrammazioni();
    initClients();
  }, [initProgrammazioni, initClients]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: page,
    }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({
      ...previous,
      pageSize: size,
      currentPage: 1,
    }));
  }, []);

  const handleFilterChange = useCallback((text: string) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: 1,
      freeText: text,
    }));
  }, []);

  const handleFieldChange = useCallback(
    (field: string) => (evt: any) => {
      setFilters((previous) => ({
        ...previous,
        [field]: evt,
      }));
    },
    []
  );

  const debounceSearchChange = useMemo(
    () => debounce(handleFilterChange, 500),
    [handleFilterChange]
  );

  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    };
  }, [debounceSearchChange]);

  const handleEditProgrammazioneClick = useCallback(
    (programmazione?: Programmazione) => () => {
      navigate(`${programmazione ? programmazione.id! : "create"}`);
    },
    [navigate]
  );

  const handleDeleteModalOpen = useCallback((programmazione: Programmazione) => {
    setSelectedProgrammazione(programmazione);
    setDeleteModal(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setSelectedProgrammazione(null);
  }, []);

  const handleDeleteProgrammazione = useCallback(() => {
    if (!selectedProgrammazione) return;
    setLoading(true);
    Programmazioni.deleteProgrammazione(selectedProgrammazione.id!.toString())
      .then(() => {
        setLoading(false);
        handleDeleteModalClose();
        showToast(
          "programmazione-deleted",
          "Programmazione eliminata con successo",
          "",
          "success"
        );
        reset();
      })
      .catch(() => {
        setLoading(false);
        showToast(
          "programmazione-delete-error",
          "Errore nell'eliminazione della programmazione",
          "",
          "danger"
        );
      });
  }, [selectedProgrammazione, showToast, reset, handleDeleteModalClose]);

  const renderToolbar = useCallback(() => {
    return (
      <>
        <Row className="align-items-center">
          <Col>
            <h4 className="mb-0">Programmazioni</h4>
          </Col>
          <Col xs={12} md="auto" className="d-none d-md-flex">
            <Space align="center">
              <AppSearch
                className="w-auto"
                placeholder="Cerca per nome..."
                onChange={debounceSearchChange}
              />
              <Select
                value={filters.userId}
                placeholder="Cerca per utente..."
                className="w-100"
                allowClear
                onChange={handleFieldChange("userId")}
              >
                {(usersOptions || []).map((userOption, index) => (
                  <Select.Option
                    key={index}
                    value={userOption.value}
                    label={userOption.label}
                  >
                    <span>{userOption.label}</span>
                  </Select.Option>
                ))}
              </Select>
            </Space>
          </Col>
          {canCreate && (
            <Col className="text-end">
              <button
                onClick={handleEditProgrammazioneClick()}
                className="button-primary"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span className="d-none d-sm-inline ms-2">
                  Nuova Programmazione
                </span>
              </button>
            </Col>
          )}
        </Row>
        <Row className="d-flex d-md-none my-3">
          <Col md="auto" className="mb-2" xs={12}>
            <AppSearch
              placeholder="Cerca per nome..."
              onChange={debounceSearchChange}
            />
          </Col>
          {/* <Col md="auto" xs={12}>
            <Select
              value={filters.muscleGroupId}
              placeholder="Cerca per gruppo muscolare..."
              className="w-100"
              onChange={handleFieldChange("muscleGroupId")}
            >
              {(muscleGroups || []).map((muscleGroup, index) => (
                <Select.Option
                  key={index}
                  value={muscleGroup.value}
                  label={muscleGroup.label}
                >
                  <span>{muscleGroup.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col md="auto" xs={12} className="my-3">
            <Select
              value={filters.motorPatternId}
              placeholder="Cerca per pattern motorio..."
              className="w-100"
              onChange={handleFieldChange("motorPatternId")}
            >
              {(motorPatterns || []).map((motorPatterns, index) => (
                <Select.Option
                  key={index}
                  value={motorPatterns.value}
                  label={motorPatterns.label}
                >
                  <span>{motorPatterns.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Col> */}
        </Row>
        <Row className="mb-4 align-items-center justify-content-center">
          <ConfigProvider locale={itIT}>
            <Pagination
              disabled={!programmazioni}
              total={total || 0}
              showTotal={(t, range) => `${range[0]}-${range[1]} di ${t}`}
              pageSize={filters.pageSize}
              current={filters.currentPage}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              showSizeChanger
              align="center"
            />
          </ConfigProvider>
        </Row>
      </>
    );
  }, [
    debounceSearchChange,
    filters,
    handleFieldChange,
    handleEditProgrammazioneClick,
    programmazioni,
    total,
    handlePageChange,
    handlePageSizeChange,
    usersOptions,
  ]);

  const renderProgrammazioni = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return programmazioni!.length === 0 ? (
      <Alert variant="info" className="py-2">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span className="ms-2">Nessuna Programmazione trovata</span>
      </Alert>
    ) : (
      <Row>
        {programmazioni!.map((programmazione, index) => (
          <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={index}>
            <ProgrammazioneCard 
              programmazione={programmazione}
              onDelete={handleDeleteModalOpen}
            />
          </Col>
        ))}
      </Row>
    );
  }, [error, programmazioni, handleDeleteModalOpen]);

  const renderAsyncContent = useCallback(() => {
    return programmazioni ? (
      renderProgrammazioni()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#9c1419" radius="1" />
      </div>
    );
  }, [programmazioni, renderProgrammazioni]);

  return (
    <Container fluid="md" className="mt-5">
      {renderToolbar()}
      <hr />
      {renderAsyncContent()}

      {/* MODALE ELIMINAZIONE */}
      <Modal show={deleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Programmazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare la programmazione{" "}
            <strong>{selectedProgrammazione?.name}</strong>?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <BsButton
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteModalClose}
          >
            Chiudi
          </BsButton>
          <BsButton variant="danger" size="sm" onClick={handleDeleteProgrammazione}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </BsButton>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default memo(ProgrammazioniList);
